import { ChangeDetectionStrategy, Component, LOCALE_ID, inject, input, output } from '@angular/core';
import { formatDate } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslationService } from '@/layout/service/translation.service';
import { translateCount } from '@/layout/service/translate-count';
import { RoomUsageAttention, RoomUsageTotals, roomAvailabilityLine, roomStatus } from '@/cloud/models/room-usage.models';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { MetricTileComponent } from '@/shared/ui/metric-tile/metric-tile.component';
import { RoomStatusPillComponent, availabilityText } from './room-status-pill.component';

/**
 * Overview "Room usage now": live counts across the rooms in scope (occupied, in a call, in a
 * meeting, booked but vacant, free, people against capacity) and a compact "Worth a look" list of
 * rooms occupied without a booking or booked but vacant.
 */
@Component({
    selector: 'app-room-usage-now',
    standalone: true,
    imports: [RouterModule, SectionCardComponent, MetricTileComponent, RoomStatusPillComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @let u = totals();
        <app-section-card class="room-usage-now" icon="fa fa-users" [title]="t()('roomUsage.now.title')" [subtitle]="u.notReporting ? count('roomUsage.now.notReporting', u.notReporting) : t()('roomUsage.now.subtitle')">
            <button actions type="button" class="room-usage-now__link" (click)="viewRooms.emit()">{{ t()('roomUsage.now.viewRooms') }} <i class="pi pi-arrow-right" aria-hidden="true"></i></button>
            <div class="room-usage-kpis">
                @if (u.withOccupancy) {
                    <app-metric-tile class="usage-kpi-occupied" icon="fa fa-user-group" [label]="t()('roomUsage.now.occupied')" [value]="u.occupied" format="1.0-0" [caption]="count('roomUsage.now.ofSensorRooms', u.withOccupancy)" />
                }
                @if (u.withConferencing) {
                    <app-metric-tile class="usage-kpi-call" icon="fa fa-video" [label]="t()('roomUsage.now.inCall')" [value]="u.inCall" format="1.0-0" [caption]="count('roomUsage.now.ofConferencingRooms', u.withConferencing)" />
                }
                @if (u.withBooking) {
                    <app-metric-tile class="usage-kpi-meeting" icon="fa fa-calendar-check" [label]="t()('roomUsage.now.inMeeting')" [value]="u.inMeeting" format="1.0-0" [caption]="count('roomUsage.now.ofCalendarRooms', u.withBooking)" />
                    @if (u.withOccupancy) {
                        <app-metric-tile
                            class="usage-kpi-booked-vacant"
                            [class.usage-kpi--warn]="u.bookedVacant > 0"
                            icon="fa fa-calendar-xmark"
                            [label]="t()('roomUsage.now.bookedVacant')"
                            [value]="u.bookedVacant"
                            format="1.0-0"
                            [caption]="count('roomUsage.now.ofCalendarRooms', u.withBooking)"
                        />
                    }
                    <app-metric-tile class="usage-kpi-free" icon="fa fa-calendar" [label]="t()('roomUsage.now.free')" [value]="u.free" format="1.0-0" [caption]="count('roomUsage.now.ofCalendarRooms', u.withBooking)" />
                }
                @if (u.withOccupancy) {
                    <app-metric-tile
                        class="usage-kpi-people"
                        icon="fa fa-person"
                        [label]="t()('roomUsage.now.people')"
                        [value]="u.people"
                        format="1.0-0"
                        [caption]="u.people === null ? t()('roomUsage.now.noCounts') : u.capacity !== null ? count('roomUsage.now.capacity', u.capacity) : null"
                    />
                }
            </div>
            @if (u.attention.length) {
                <div class="room-usage-attention">
                    <h3>{{ t()('roomUsage.attention.title') }}</h3>
                    <ul>
                        @for (item of u.attention; track item.id) {
                            <li>
                                <a [routerLink]="['/dashboard', namespaceId(), item.id]">
                                    <span class="room-usage-attention__room">
                                        <span class="room-usage-attention__name">{{ item.displayName }}</span>
                                        @if (item.roomType) {
                                            <span class="room-usage-attention__type">{{ t()('roomType.' + item.roomType) }}</span>
                                        }
                                    </span>
                                    <app-room-status-pill
                                        class="room-usage-attention__reason"
                                        [attr.data-reason]="item.reason"
                                        [status]="item.reason === 'noShow' ? 'bookedVacant' : attentionStatus(item)"
                                        [label]="t()('roomUsage.attention.' + item.reason)"
                                    />
                                    <span class="room-usage-attention__detail">{{ detail(item) }}</span>
                                </a>
                            </li>
                        }
                    </ul>
                    @if (u.attentionTotal > u.attention.length) {
                        <p class="room-usage-attention__more">{{ count('roomUsage.attention.more', u.attentionTotal - u.attention.length) }}</p>
                    }
                </div>
            }
        </app-section-card>
    `,
    styles: [
        `
            :host {
                display: block;
                margin-top: 1rem;
            }
            .room-usage-now__link {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
                padding: 0.4rem 0.6rem;
                border: 0;
                border-radius: var(--content-border-radius);
                background: transparent;
                color: var(--primary-color);
                font: inherit;
                font-size: 0.875rem;
                font-weight: 500;
                cursor: pointer;
                white-space: nowrap;
            }
            .room-usage-now__link:hover {
                background: var(--surface-hover);
            }
            .room-usage-now__link:focus-visible {
                outline: 2px solid var(--primary-color);
                outline-offset: 2px;
            }
            .room-usage-kpis {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(min(100%, 9.5rem), 1fr));
                gap: var(--app-space-3);
            }
            .usage-kpi--warn {
                /* Amber border and value; metric-tile reads these tokens. */
                --surface-border: color-mix(in srgb, var(--app-status-warning) 55%, transparent);
                --text-color: var(--app-status-warning-fg);
            }
            .room-usage-attention {
                margin-top: var(--app-space-4);
            }
            .room-usage-attention h3 {
                margin: 0 0 var(--app-space-2);
                font-size: 0.875rem;
                font-weight: 600;
            }
            .room-usage-attention ul {
                display: flex;
                flex-direction: column;
                margin: 0;
                padding: 0;
                list-style: none;
                border: 1px solid var(--surface-border);
                border-radius: var(--content-border-radius);
                overflow: hidden;
            }
            .room-usage-attention li + li {
                border-top: 1px solid var(--surface-border);
            }
            .room-usage-attention a {
                display: grid;
                grid-template-columns: minmax(0, 14rem) minmax(0, 13rem) minmax(0, 1fr);
                align-items: center;
                gap: var(--app-space-3);
                padding: var(--app-space-2) var(--app-space-3);
                color: var(--text-color);
                text-decoration: none;
                font-size: 0.875rem;
            }
            .room-usage-attention a:hover {
                background: var(--surface-hover);
            }
            .room-usage-attention a:focus-visible {
                outline: 2px solid var(--primary-color);
                outline-offset: -2px;
            }
            .room-usage-attention__room {
                display: flex;
                flex-direction: column;
                min-width: 0;
            }
            .room-usage-attention__name {
                font-weight: 600;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .room-usage-attention__type {
                color: var(--text-color-secondary);
                font-size: 0.75rem;
            }
            .room-usage-attention__reason {
                justify-self: start;
            }
            .room-usage-attention__detail {
                min-width: 0;
                color: var(--text-color-secondary);
                font-size: 0.8125rem;
                overflow-wrap: anywhere;
                font-variant-numeric: tabular-nums;
            }
            .room-usage-attention__more {
                margin: var(--app-space-2) 0 0;
                color: var(--text-color-secondary);
                font-size: 0.8125rem;
            }
            @media (max-width: 640px) {
                .room-usage-attention a {
                    grid-template-columns: minmax(0, 1fr) auto;
                    gap: var(--app-space-1) var(--app-space-2);
                }
                .room-usage-attention__detail {
                    grid-column: 1 / -1;
                }
            }
        `
    ]
})
export class RoomUsageNowComponent {
    readonly t = inject(TranslationService).t;
    private locale = inject(LOCALE_ID);

    readonly totals = input.required<RoomUsageTotals>();
    readonly namespaceId = input<string | null>(null);
    readonly viewRooms = output<void>();

    count(key: string, n: number | null): string {
        return translateCount(this.t(), key, n);
    }

    attentionStatus(item: RoomUsageAttention) {
        return roomStatus(item.usage);
    }

    /** "Private meeting · Booked until 6:44 AM · 0 / 8 people", "Free until 14:00 · 2 / 4 people". */
    detail(item: RoomUsageAttention): string {
        const time = (iso: string) => formatDate(iso, 'shortTime', this.locale);
        const parts: string[] = [];
        const line = roomAvailabilityLine(item.usage);
        if (line?.kind === 'bookedUntil') {
            if (line.title) parts.push(line.title);
            parts.push(`${this.t()('roomUsage.bookedUntil')} ${time(line.time)}`);
        } else {
            const text = availabilityText(line, this.t(), time);
            if (text) parts.push(text);
        }
        const people = item.usage.peopleCount;
        if (typeof people === 'number') parts.push(`${people}${item.capacity !== null ? ` / ${item.capacity}` : ''} ${this.t()('roomUsage.people')}`);
        else if (item.capacity !== null) parts.push(translateCount(this.t(), 'roomCapacity.seats', item.capacity));
        return parts.join(' · ');
    }
}
